/** Session storage key holding when this tab last reloaded after a failed dynamic import, in epoch ms. */
const LAST_RELOAD_KEY = 'preloadErrorReloadedAt';

const MIN_RELOAD_INTERVAL_MS = 60 * 1000;

const findLastReloadTime = (): number | undefined => {
    const lastReload = sessionStorage.getItem(LAST_RELOAD_KEY);

    return lastReload === null ? undefined : Number(lastReload);
};

/**
 * Handles Vite's `vite:preloadError` event by reloading the page, so a tab whose chunks are no longer
 * available picks up a fresh `index.html`. Reloads at most once a minute per tab, and never while offline
 * or when session storage is unavailable. When it doesn't reload, the import error propagates as normal.
 */
export const reloadOnPreloadError = (event: Event, reload = (): void => window.location.reload()): void => {
    if (!navigator.onLine) {
        return;
    }

    try {
        const lastReloadTime = findLastReloadTime();

        if (lastReloadTime !== undefined && Date.now() - lastReloadTime < MIN_RELOAD_INTERVAL_MS) {
            return;
        }

        sessionStorage.setItem(LAST_RELOAD_KEY, `${Date.now()}`);
    } catch {
        /** Without storage we can't tell a reload loop apart from a first failure, so don't reload. */
        return;
    }

    event.preventDefault();
    reload();
};
